import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { queryClient } from '@app/queryClient'
import { authService } from '../services/authService'
import { isImpersonationActive } from '../utils/impersonationStorage'
import type { User, LoginRequest, AuthResponse } from '../types/auth'

interface AuthState {
  user: User | null
  activeOrganizationId: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  login: (data: LoginRequest) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  clearError: () => void
  setUser: (user: User | null) => void
  setActiveOrganizationId: (organizationId: string | null) => void
}

export const useAuthStore = create<AuthState>()(
  devtools(
    set => ({
      user: null,
      activeOrganizationId: authService.getActiveOrganizationId(),
      isAuthenticated: false,
      isLoading: true,
      error: null,

      login: async (data: LoginRequest) => {
        set({ isLoading: true, error: null })
        try {
          const response: AuthResponse = await authService.login(data)
          authService.saveSession(response)
          const activeOrganizationId = authService.getActiveOrganizationId()

          set({
            user: response.user,
            activeOrganizationId,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Неверный email или пароль'
          set({ isLoading: false, error: message })
          throw error
        }
      },

      logout: () => {
        authService.logout()
        queryClient.clear()
        set({
          user: null,
          activeOrganizationId: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      },

      checkAuth: async () => {
        const token = authService.getAccessToken()
        if (!token) {
          set({
            user: null,
            activeOrganizationId: null,
            isLoading: false,
            isAuthenticated: false,
          })
          return
        }

        // Режим просмотра: /auth/me вернул бы реального суперадмина и перетёр
        // viewer-роль (админ-меню с 403-тостами). Пока imp активен — доверяем
        // сохранённой viewer-сессии; истечение токена обработает интерцептор.
        if (isImpersonationActive()) {
          const storedUser = authService.getStoredUser()
          if (storedUser) {
            set({
              user: storedUser,
              activeOrganizationId: authService.getActiveOrganizationId(),
              isAuthenticated: true,
              isLoading: false,
            })
            return
          }
        }

        try {
          const user = await authService.getMe()
          authService.saveUser(user)
          const nextOrganizationId = authService.getActiveOrganizationId()
          set({
            user,
            activeOrganizationId: nextOrganizationId,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch {
          authService.logout()
          queryClient.clear()
          set({
            user: null,
            activeOrganizationId: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user: User | null) => {
        if (user) {
          authService.saveUser(user)
        }
        set({
          user,
          activeOrganizationId: authService.getActiveOrganizationId(),
          isAuthenticated: !!user,
        })
      },

      setActiveOrganizationId: (organizationId: string | null) => {
        authService.setActiveOrganizationId(organizationId)
        set({ activeOrganizationId: organizationId })
      },
    }),
    { name: 'auth-store' }
  )
)
